import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { COSTE_POR_PLATO_IMG } from '@/features/aprender/learnArticleConstants'
import { DashList, LearnArticleLink, LearnSeriesNav, TipiLink } from '@/features/aprender/learnArticleShared'
import { LandingDemoPlayer } from '@/features/demos/LandingDemoPlayer'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const IMG = COSTE_POR_PLATO_IMG

const k = (field: string) => `learn.articles.como-calcular-coste-de-un-plato.${field}`

const SOURCES: { labelKey: string; fallback: string }[] = [
    {
        labelKey: 'sources.1',
        fallback:
            'Historia de Carlos y cifras 3 € vs 3,95 €, 0,95 € × 500 × 12 = 5.700 €: ejemplo ficticio de Tipi para ilustrar el coste a ojo. No es el dato de un local concreto.',
    },
    {
        labelKey: 'sources.2',
        fallback:
            'Definición operativa de escandallo (ficha con ingredientes, cantidades, precios, mermas y coste por ración): criterio de control de costes en restauración independiente usado en esta serie Tipi.',
    },
]

export function CostePorPlatoArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'creencia', label: t(k('toc.1'), '1. «Lo vendo a tres veces el coste»') },
        { id: 'historia-carlos', label: t(k('toc.2'), '2. El plato estrella que mentía') },
        { id: 'que-es-escandallo', label: t(k('toc.3'), '3. Qué es un escandallo (y para qué sirve)') },
        { id: 'limites', label: t(k('toc.4'), '4. Lo que el coste por plato no arregla') },
        { id: 'por-donde-seguir', label: t(k('toc.5'), '5. Cómo seguir leyendo esta serie') },
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'Puedes tener el comedor lleno todos los fines de semana y seguir ganando menos de lo que crees. La diferencia suele estar en un documento que muchos hosteleros conocen, pero pocos revisan de verdad: el escandallo.',
                            )}
                        </>
                    }
                    imageSrc={`${IMG}/hero-facturas.webp`}
                    imageAlt={t(
                        k('heroAlt'),
                        'Hostelero revisando facturas, albaranes y una libreta de números tras el cierre del servicio',
                    )}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <>
                            <div className={articleUi.proseIntro}>
                                <p className={cn(articleUi.p, articleUi.pGapMd, 'text-article-muted-soft')}>
                                    {t(k('updated'), 'Actualizado el 13 de septiembre de 2026.')}
                                </p>
                                <p className={articleUi.p}>
                                    {t(
                                        k('intro1'),
                                        '«Si lo vendo a más del triple de lo que cuesta, hay margen de sobra.» Esa frase la hemos dicho todos. Suena sólida. Hasta que descubres que el «lo que cuesta» era un cálculo a ojo de hace dos años.',
                                    )}
                                </p>
                                <p className={articleUi.p}>
                                    {t(
                                        k('intro2'),
                                        'Un cliente te lo dejó caído en la barra, sin malicia: «Oye, ¿tú sabes de verdad cuánto te deja esa hamburguesa?» No era un ataque. Era la pregunta que evitabas porque el comedor estaba lleno y la sensación era buena.',
                                    )}
                                </p>
                                <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                                    {t(
                                        k('intro3'),
                                        'La respuesta puede estar escondida en el plato que más sale. El que te enorgullece. El que parece rentable porque lo cobras «bastante más» de lo que crees que cuesta.',
                                    )}
                                </p>
                            </div>

                            <blockquote className={articleUi.quote}>
                                {t(k('quote1'), '«¿Cómo puede ser que venda tanto y me quede tan poco?»')}
                            </blockquote>
                        </>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='creencia' className={articleUi.h2}>
                        {t(k('h2.creencia'), 'La creencia del triple y la pega del «más o menos»')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p1'),
                                'La regla del triple (o del food cost «bonito») solo funciona si el coste de partida es real. Si el coste está incompleto, el múltiplo es teatro. La pega va primero: sin gramajes, merma y precios al día, cualquier margen que digas es una opinión.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p2'),
                                'Una cosa es lo que piensas que cuesta un plato. Otra es lo que realmente te cuesta ponerlo delante del cliente.',
                            )}
                        </p>
                    </div>

                    <h2 id='historia-carlos' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.carlos'), 'El plato más vendido también puede ser el que más dinero te hace perder')}
                    </h2>
                    <h3 className={articleUi.h3}>
                        {t(k('h3.carlos'), 'La historia de Carlos y su hamburguesa estrella')}
                    </h3>
                    <div className={articleUi.prose}>
                        <p className={articleUi.pItalic}>
                            {t(
                                k('italic1'),
                                'La siguiente historia es ficticia, pero representa una situación demasiado habitual en la hostelería.',
                            )}
                        </p>
                        <p className={articleUi.p}>
                            {t(
                                k('p3'),
                                'Carlos tenía una hamburguesa que era la estrella de su restaurante. Pan brioche, carne de vacuno, queso, bacon, salsa casera y patatas. La vendía a 11 € y salían cientos todos los meses. Cuando creó la receta, hizo un cálculo rápido y llegó a la conclusión de que preparar aquella hamburguesa le costaba aproximadamente 3 €.',
                            )}
                        </p>
                        <p className={articleUi.p}>
                            {t(
                                k('p4'),
                                'Así que pensó: «La vendo por más de tres veces su coste. Aquí hay margen de sobra». Y siguió vendiéndola. Una detrás de otra.',
                            )}
                        </p>
                    </div>

                    <div className={articleUi.figure}>
                        <div className={articleUi.figureFrame3x2}>
                            <img
                                src={`${IMG}/hamburguesa.webp`}
                                alt={t(k('burgerAlt'), 'Hamburguesa real servida en un restaurante, con patatas')}
                            />
                        </div>
                    </div>

                    <div className={articleUi.prose}>
                        <p className={articleUi.p}>
                            {t(
                                k('p5'),
                                'El problema es que aquel coste estaba calculado a ojo. No había contado correctamente la salsa. Ni el aceite de la freidora. Ni toda la guarnición. Ni la merma de algunos ingredientes. Tampoco había actualizado el precio de la carne después de varias subidas del proveedor.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapXl)}>
                            {t(
                                k('p6'),
                                'Cuando por fin se sentó a calcularlo de verdad, descubrió que la hamburguesa no le costaba 3 €. Le costaba 3,95 €. La diferencia era de 0,95 € por unidad.',
                            )}
                        </p>
                    </div>

                    <div className={articleUi.stats}>
                        <div className={articleUi.stat}>
                            <p className={articleUi.statLabel}>{t(k('stat.eye'), 'Coste a ojo')}</p>
                            <p className={articleUi.statValue}>3,00 €</p>
                        </div>
                        <div className={articleUi.stat}>
                            <p className={articleUi.statLabel}>{t(k('stat.real'), 'Coste real')}</p>
                            <p className={articleUi.statValue}>3,95 €</p>
                        </div>
                        <div className={articleUi.statAlert}>
                            <p className={articleUi.statLabelAlert}>{t(k('stat.diff'), 'Diferencia / ud.')}</p>
                            <p className={articleUi.statValueAlert}>0,95 €</p>
                        </div>
                        <div className={articleUi.statAlert}>
                            <p className={articleUi.statLabelAlert}>{t(k('stat.month'), 'Impacto mensual')}</p>
                            <p className={articleUi.statValueAlert}>475 €</p>
                        </div>
                        <div className={articleUi.statAlert}>
                            <p className={articleUi.statLabelAlert}>{t(k('stat.year'), 'Impacto anual')}</p>
                            <p className={articleUi.statValueAlert}>5.700 €</p>
                        </div>
                    </div>

                    <div className={articleUi.prose}>
                        <p className={articleUi.p}>
                            {t(
                                k('p7'),
                                'Puede que 0,95 € no te parezcan una barbaridad. Pero Carlos vendía 500 hamburguesas al mes. 0,95 € × 500 = 475 € al mes. En doce meses: 5.700 € al año.',
                            )}
                        </p>
                        <p className={articleUi.p}>
                            {t(
                                k('p8'),
                                'Carlos no estaba perdiendo necesariamente 5.700 € en caja. Pero sí estaba ganando 5.700 € menos de lo que creía con un solo plato. Ahora imagina el mismo error en cinco, diez o veinte recetas.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p9'),
                                'Carlos no tenía un problema de ventas. Tenía un problema de control. Puedes tener el restaurante lleno y un negocio poco rentable. Vender mucho no significa ganar mucho.',
                            )}
                        </p>
                    </div>

                    <h2 id='que-es-escandallo' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(
                            k('h2.escandallo'),
                            'Para saber cuánto ganas, primero tienes que saber cuánto te cuesta vender',
                        )}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={articleUi.p}>
                            {t(
                                k('p10'),
                                'Calcular el coste de cada plato no es un hobby de calculadora. Es saber si cada venta genera margen o solo mueve producto y paga facturas.',
                            )}
                        </p>
                        <p className={articleUi.p}>
                            {t(
                                k('p11BeforeStrong'),
                                'El cálculo que permite saber cuánto cuesta realmente preparar cada receta se llama ',
                            )}
                            <strong>{t(k('p11Strong'), 'escandallo')}</strong>
                            {t(
                                k('p11AfterStrong'),
                                '. Es uno de los pilares de un restaurante bien gestionado: gramajes reales, precios al día y un número por ración que puedas defender.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapSm)}>
                            {t(
                                k('p12'),
                                'Un escandallo es el cálculo de lo que te cuesta de verdad cada ración, y para que funcione tiene que recoger:',
                            )}
                        </p>
                        <DashList
                            items={[
                                t(k('list.escandallo.1'), 'Los ingredientes de una receta.'),
                                t(k('list.escandallo.2'), 'La cantidad utilizada de cada ingrediente.'),
                                t(k('list.escandallo.3'), 'Su precio de compra.'),
                                t(k('list.escandallo.4'), 'Las mermas o rendimientos.'),
                                t(k('list.escandallo.5'), 'El coste total de elaboración.'),
                                t(k('list.escandallo.6'), 'El coste final por ración.'),
                            ]}
                        />
                        <p className={articleUi.p}>
                            {t(
                                k('p12LinkBefore'),
                                'Qué es exactamente, qué no es y por qué caduca con cada albarán lo explicamos en ',
                            )}
                            <LearnArticleLink slug='que-es-un-escandallo'>
                                {t(k('linkQueEs'), 'qué es un escandallo')}
                            </LearnArticleLink>
                            {t(k('p12LinkAfter'), '.')}
                        </p>
                        <p className={articleUi.p}>
                            {t(
                                k('p13'),
                                'Con esa ficha sabes cuánto deja cada plato, mantienes cantidades, reduces desperdicio y fijas precios con datos. No con lo que cobra el de al lado ni con el «yo creo que vamos bien».',
                            )}
                        </p>
                    </div>

                    <blockquote className={articleUi.quoteSm}>
                        {t(k('quote2'), '«El escandallo es la radiografía económica de tu plato.»')}
                    </blockquote>

                    <div className={articleUi.split}>
                        <div>
                            <p className={articleUi.p}>
                                {t(
                                    k('p14'),
                                    'Te enseña lo que el cliente no ve: cuánto dinero hay dentro de esa elaboración antes de que salga por el pase.',
                                )}
                            </p>
                            <p className={articleUi.p}>
                                {t(k('p15'), 'Puedes hacerlo en Excel, en papel o con una herramienta como ')}
                                <TipiLink />.
                            </p>
                            <p className={articleUi.p}>
                                {t(
                                    k('p16'),
                                    'Lo importante no es tanto dónde lo haces, sino gramajes reales, precios actualizados y revisión cada vez que algo cambia.',
                                )}
                            </p>
                            <p className={articleUi.p} style={{ marginBottom: 0 }}>
                                {t(
                                    k('p17'),
                                    'Ningún escandallo sirve si se construye con precios antiguos o cantidades inventadas.',
                                )}
                            </p>
                        </div>
                        <div className={articleUi.figureFrameAuto}>
                            <img
                                src={`${IMG}/oidotipi-screenshot-croquetas.jpg`}
                                alt={t(
                                    k('tipAlt'),
                                    'Pantalla de la app Tipi mostrando el escandallo de las croquetas de jamón: PVP, beneficio, raciones y coste por ración',
                                )}
                            />
                        </div>
                    </div>

                    <h2 id='limites' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.limites'), 'Lo que el coste por plato no arregla')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.limites.1'),
                                'Saber el coste no baja el alquiler ni arregla sola una plantilla imposible. Tampoco garantiza que cocina respete la ficha: para eso hace falta contrastar teórico y real.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.limites.2'),
                                'Lo que sí arregla es dejar de decidir a ciegas. Sin coste por ración, el food cost, el margen y la carta rentable son castillos en el aire.',
                            )}
                        </p>
                    </div>

                    <h2 id='por-donde-seguir' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.seguir'), 'No necesitas aprenderlo todo de una vez')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p18'),
                                'Esta guía está en piezas cortas. Empieza por el cálculo práctico y sigue con métricas y control:',
                            )}
                        </p>
                        <DashList
                            variant='tight'
                            items={[
                                t(
                                    k('list.seguir.1'),
                                    'Cómo hacer el escandallo paso a paso, con el ejemplo de la hamburguesa.',
                                ),
                                t(
                                    k('list.seguir.2'),
                                    'Food cost y margen bruto: cuánto ganas realmente con cada venta.',
                                ),
                                t(
                                    k('list.seguir.3'),
                                    'Coste teórico vs coste real y los errores que hacen mentir al escandallo.',
                                ),
                                t(
                                    k('list.seguir.4'),
                                    'Cómo diseñar una carta más rentable sin escandallar toda la carta en un día.',
                                ),
                            ]}
                        />
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p19Before'), 'Empieza por ')}
                            <LearnArticleLink slug='como-hacer-un-escandallo-paso-a-paso'>
                                {t(k('linkEscandallo'), 'el escandallo paso a paso')}
                            </LearnArticleLink>
                            {t(
                                k('p19Mid'),
                                ' si todavía no has calculado el coste de tus platos estrella. Si ya tienes fichas, salta a ',
                            )}
                            <LearnArticleLink slug='food-cost-y-margen-bruto-restaurante'>
                                {t(k('linkFoodCost'), 'food cost y margen bruto')}
                            </LearnArticleLink>
                            {t(k('p19After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.ctaInline'),
                                'Si el plato estrella te deja menos de lo que creías, dínoslo y le ponemos números.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('closing.signoff'),
                                'Pesa un plato esta semana. El resto de la carta puede esperar. El margen del bestseller, no.',
                            )}
                        </p>
                    </div>

                    <section id='escandallo-por-voz'>
                        <h2 className={cn(articleUi.h2, articleUi.h2Spaced)}>
                            {t(k('video.title'), 'Atajo: dicta la receta y Tipi saca el coste')}
                        </h2>
                        <p className={articleUi.p}>
                            {t(
                                k('video.intro'),
                                'Si no quieres pelearte con la libreta, cuéntale el plato a Tipi como a un compañero de cocina.',
                            )}
                        </p>
                        <LandingDemoPlayer demoId='escandallo-voz' variant='article' />
                    </section>
                </ArticleReadingLayout>

                <section className={articleUi.cta}>
                    <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>
                        {t(k('h2.cta'), '¿Tu bestseller te deja menos de lo que creías?')}
                    </h2>
                    <p className={articleUi.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Con Tipi calculas el coste real del plato y dejas de trabajar con el triple a ojo. Dínoslo y le ponemos números.',
                        )}
                    </p>
                    <div className={articleUi.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid)}>
                            {t(k('cta.button'), 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                </section>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        {SOURCES.map(source => (
                            <li key={source.labelKey}>{t(k(source.labelKey), source.fallback)}</li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
